<script setup lang="ts">
import type { ProjectLog } from '#trade-activity/queries/logs'
import { formatValue } from '@forgd/formatter'
import { Avatar, AvatarInitials } from '@forgd/ui-kit/avatar'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { PencilIcon } from '@heroicons/vue/24/outline'

interface TaLogCardProps {
  log: ProjectLog
}

const props = defineProps<TaLogCardProps>()

const emit = defineEmits<{
  edit: [log: ProjectLog]
}>()

const auth = useAuth()
const isEditable = computed(() => props.log.user.id === auth.me?.id)
const hasName = computed(() => props.log.user.firstName !== null && props.log.user.lastName !== null)
</script>

<template>
  <div class="flex gap-2 rounded-lg bg-tertiary p-4">
    <div class="flex h-full w-2.5 flex-col items-center gap-2">
      <div class="mt-1.5 size-2.5 shrink-0 rounded-full bg-accent" />
      <div class="h-full w-px bg-neutral-600" />
    </div>
    <div class="flex w-full flex-col gap-4">
      <div class="flex w-full justify-between">
        <div class="flex h-6 items-center gap-2">
          <div class="space-x-2 text-xs text-secondary">
            <span>{{ formatValue(log.createdAt, 'date') }}</span>
            <span v-if="log.updatedAt && log.createdAt !== log.updatedAt" class="text-placeholder">Last edited: {{ formatValue(log.updatedAt, 'date') }}</span>
          </div>
          <Badge variant="neutral" size="sm">
            {{ log.category.name }}
          </Badge>
        </div>
        <Button
          v-if="isEditable"
          variant="ghost"
          size="xs"
          icon-only
          aria-label="Edit log"
          @click="emit('edit', log)"
        >
          <PencilIcon />
        </Button>
      </div>
      <div class="text-sm whitespace-pre-line">
        {{ log.content }}
      </div>
      <div class="flex items-center gap-2">
        <Avatar size="xs" bordered>
          <AvatarInitials :first-name="log.user.firstName" :last-name="log.user.lastName" />
        </Avatar>
        <div class="text-xs font-semibold text-primal">
          <span v-if="hasName">{{ log.user.firstName }} {{ log.user.lastName }}</span>
          <span v-else>{{ auth.project?.name }} Teammate</span>
        </div>
      </div>
    </div>
  </div>
</template>
